/* StateViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./StateViewRenderer");
const _ = require("lodash");

class StateViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        viewState,
        io,
        solutionsManager,
        deviceConfiguration,
        storageFactory,
        DeviceEntity
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._io = io;
        this._DeviceConfigurationEntity = deviceConfiguration;
        this._storageFactory = storageFactory;
        this._deviceEntityDef = DeviceEntity;
        this._userDataStorage = this._storageFactory.getRemoteStorageInstance(
            "view",
            "device_config"
        );

        this._config = {};
        // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
        // To be refactored with separate views once form layout / style specifications have matured
        this._solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = this._solutionDefinition.name || "";

        this._viewState = viewState;

        this._deviceConfigFeed = undefined;
        this._deviceConfigCollection = undefined;
        this._rawConfigCollection = {};

        this._selectedDeviceConfigOption = undefined;
        this._deviceConfigViewModel = [];
        this._deviceName = undefined;

        this._registerListeners();
        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerDeviceFeedListeners() {
        this._device.on(
            "NamePropertyUpdated",
            (this._onNamePropUpdated = this._onNamePropUpdated.bind(this))
        );
        this._gameEditor._deviceConfigFeed.on(
            "EntityAdded",
            (this._onEntityAdded = this._onEntityAdded.bind(this))
        );
        this._gameEditor._deviceConfigFeed.on(
            "EntityRemoved",
            (this._onEntityRemoved = this._onEntityRemoved.bind(this))
        );
        this._gameEditor._deviceConfigFeed.on(
            "EntityPropertiesUpdated",
            (this._onEntityUpdated = this._onEntityUpdated.bind(this))
        );
    }

    _unregisterDeviceFeedListeners() {
        this._device.removeListener("NamePropertyUpdated", this._onNamePropUpdated);
        this._gameEditor._deviceConfigFeed.removeListener("EntityAdded", this._onEntityAdded);
        this._gameEditor._deviceConfigFeed.removeListener("EntityRemoved", this._onEntityRemoved);
        this._gameEditor._deviceConfigFeed.removeListener(
            "EntityPropertiesUpdated",
            this._onEntityUpdated
        );
    }

    _onNamePropUpdated(name) {
        this._deviceConfigViewModel = [];
        this._deviceName = name;
        this._buildViewModel();
        this.emit("ViewModelReset");
    }

    _onEntityAdded(devConf) {
        this._deviceConfigCollection[devConf.id] = devConf;

        let {marshalledObj} = this._marshallDeviceData(devConf);
        this._deviceConfigViewModel.push(marshalledObj);

        this.emit("DeviceConfAdded", /*marshalledObj,*/ devConf.id);
    }

    _onEntityRemoved(devConf) {
        _.unset(this._deviceConfigCollection, devConf.id);
        _.unset(this._rawConfigCollection, devConf.id);
        let {marshalledObj} = this._marshallDeviceData(devConf);
        let idx = this._deviceConfigViewModel.indexOf(marshalledObj);
        this._deviceConfigViewModel.splice(idx, 1);
        this.emit("DeviceConfRemoved", marshalledObj);
    }

    _onEntityUpdated(devConf) {
        this._deviceConfigCollection[devConf.id] = devConf;
        let {marshalledObj} = this._marshallDeviceData(devConf);

        let currentObj = this._deviceConfigViewModel.find((obj) => {
            if (obj.id === devConf.id) {
                return true;
            }
        });
        let idx = this._deviceConfigViewModel.indexOf(currentObj);
        this._deviceConfigViewModel.splice(idx, 1, marshalledObj);

        this.emit("DeviceConfUpdated", /*marshalledObj,*/ devConf.id);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get io() {
        return this._io;
    }

    get selectedDeviceConfigOption() {
        return this.getSelectedDeviceConfigOption();
    }

    set selectedDeviceConfigOption(val) {
        this.setSelectedDeviceConfigOption(val);
    }

    getSelectedDeviceConfigOption() {
        return this._selectedDeviceConfigOption;
    }

    setSelectedDeviceConfigOption(val, silent) {
        this._viewType = "text"; //this._userDataStorage.getData("viewType", true);
        this._selectedDeviceConfigOption = val;
        if (!silent) {
            this.emit("DeviceConfigOptionSelected", val);
        }
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    async whenModelIsReady() {
        return this._gameEditor
            .whenDeviceIsReady()
            .then(() => this._gameEditor.whenCommandSetsAreReady())
            .then(() => {
                this._device = this._gameEditor.getDevice();
                this._deviceName = this._device.name;
                return this._gameEditor.whenDeviceConfigIsReady();
            })
            .then(() => {
                this.selectedId = _.get(this._viewState, "selectedConfigId", false);
                this._gameEditor.emit("ClearViewState");
                this._deviceConfigFeed = this._gameEditor.getDeviceConfig();
                return Promise.resolve();
            })
            .then(() => {
                let filter = {};
                if (
                    _.get(this._gameEditor._solutionManager, "_activeSolution.name") ===
                    "config_drift"
                ) {
                    filter = !_.isEmpty(this._device)
                        ? {$and: [{deviceId: this._device.getId()}, {fromCapture: true}]}
                        : {};
                } else {
                    filter = !_.isEmpty(this._device) ? {deviceId: this._device.getId()} : {};
                }

                return this._DeviceConfigurationEntity.loadDeviceConfigurations(
                    this._gameEditor._activeOrgId,
                    filter,
                    {},
                    {_id: -1},
                    {
                        skip: 0,
                        limit: 2000,
                    }
                );

                // if(!_.isEmpty(deviceConfigs)) {
                //     return this._device.loadRelatedDeviceConfigurations({}, {}, {createdOn: -1}, {
                //         "skip": 0,
                //         "limit": 2000
                //     });
                // } else {
                //     return this._device.loadRelatedDiscoveredConfig({}, {}, {createdOn: -1}, {
                //         "skip": 0,
                //         "limit": 2000
                //     });
                // }
            })
            .then((configList) => {
                this._deviceConfigList = configList;
                this._registerDeviceFeedListeners();
                this._deviceConfigCollection = {};
                this._deviceConfigList.forEach((i) =>
                    Object.assign(this._deviceConfigCollection, {[i.getId()]: i})
                );
                this._buildViewModel(this._deviceConfigCollection);
            })
            .then(() => {
                return this.getSelectedDeviceConfig();
            })
            .then((config) => {
                if (config) {
                    this._config = config.getCapture() || {};
                }
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceConfigurationSnapshotViewComponent.whenModelIsReady error."
                );
            });
    }

    shutdown() {
        if (!_.isUndefined(this._gameEditor._deviceConfigFeed)) {
            this._unregisterDeviceFeedListeners();
        }
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._renderer = undefined;
                this._logger = undefined;
                this._renderer = undefined;
                this._io = undefined;
                this._viewState = undefined;
                this._deviceConfigFeed = undefined;
                this._deviceConfigCollection = undefined;
                this._deviceConfigList = undefined;
                this._rawConfigCollection = undefined;
                this._selectedDeviceConfigOption = undefined;
                this._deviceConfigViewModel = undefined;
                this._deviceName = undefined;
                this._DeviceConfigurationEntity = undefined;
                this._gameEditor.emit("DeviceConfigShutdown");
                this._gameEditor = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "StateViewComponent.shutdown() error.");
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    _buildViewModel(cache) {
        this._deviceConfigViewModel = [];
        // let stableObj;
        let selectedObj;
        let selectedId = this.selectedId;
        let time = 0;
        let latest;
        Object.keys(cache).forEach((id) => {
            let {marshalledObj, timestamp} = this._marshallDeviceData(cache[id]);
            if (timestamp > time) {
                time = timestamp;
                latest = marshalledObj;
            }
            this._deviceConfigViewModel.push(marshalledObj);
            // if (marshalledObj.value.isStable) {
            //     stableObj = marshalledObj;
            // }
            if (selectedId && selectedId === id) {
                selectedObj = marshalledObj;
            }
        });
        // const latest = this._deviceConfigViewModel[this._deviceConfigViewModel.length - 1];
        if (selectedObj) {
            this.setSelectedDeviceConfigOption(selectedObj.value, true);
        } else {
            if (latest) {
                this.setSelectedDeviceConfigOption(latest.value, true);
            }
        }
    }

    _marshallDeviceData(devConf) {
        let marshalledObj = {
            value: {
                id: devConf.id,
                isStable: devConf.isStableDeviceConfiguration,
                wasDifferentAtCaptureTime: devConf.wasDifferentAtCaptureTime,
                fromCapture: devConf.fromCapture,
            },
        };
        const isValidDate = (value) => value && !_.isNaN(new Date(value).getTime());
        let localDateTime = "";
        let timestamp;

        if (isValidDate(devConf.startTime)) {
            timestamp = devConf.startTime;
        } else if (isValidDate(devConf.capturedTime)) {
            timestamp = devConf.capturedTime;
        } else if (isValidDate(devConf.updatedOn)) {
            timestamp = devConf.updatedOn;
        }

        // Fallback if in DM or DE
        if (
            !timestamp &&
            (this._solutionName === "device_manager" || this._solutionName === "data_explorer") &&
            isValidDate(this._device.discoveredOn)
        ) {
            timestamp = this._device.discoveredOn;
        }

        timestamp = new Date(timestamp);
        marshalledObj.value.timestamp = timestamp;
        localDateTime = timestamp.toLocaleDateString() + " " + timestamp.toLocaleTimeString();
        if (devConf.description) {
            marshalledObj.label = devConf.description + " - " + localDateTime;
            marshalledObj.value.fileName = devConf.description + " - " + localDateTime;
        } else {
            marshalledObj.label = localDateTime;
            marshalledObj.value.fileName = localDateTime;
        }

        marshalledObj.label +=
            (devConf.initiatorUser ? " (" + devConf.initiatorUser : " (") +
            (devConf.initiatorIp ? " from " + devConf.initiatorIp + ")" : ")");
        marshalledObj.value.fileName +=
            (devConf.initiatorUser ? " (" + devConf.initiatorUser : " (") +
            (devConf.initiatorIp ? " from " + devConf.initiatorIp + ")" : ")");

        if (!devConf.initiatorUser && !devConf.initiatorIp) {
            marshalledObj.label = marshalledObj.label.slice(0, -3);
            marshalledObj.value.fileName = marshalledObj.value.fileName.slice(0, -3);
        }

        return {marshalledObj, timestamp};
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/
    getSelectedDeviceConfig() {
        return this._device.loadRelatedStateData({capture: true});
        // let config;
        // let rawConfig;
        // let configIds = [];
        // let configId = _.get(this._selectedDeviceConfigOption, "id", undefined);

        // if (!_.isEmpty(this._deviceConfigCollection)) {
        //     if (!configId) {
        //         configIds = Object.keys(this._deviceConfigCollection);
        //         configId = configIds[configIds.length - 1];
        //     }
        //     config = this._deviceConfigCollection[configId];
        //     rawConfig = this._rawConfigCollection[configId];
        // }

        // // Request device rawConfiguration from the server if we don't have it yet
        // if (config && !rawConfig) {
        //     return this.loadRawConfiguration(config).then(
        //         (rawConfig) => {
        //             this._rawConfigCollection[configId] = rawConfig;
        //             return Promise.resolve(rawConfig);
        //         },
        //         (err) => console.log(err)
        //     );
        // }
        // return Promise.resolve(rawConfig);
    }

    loadRawConfiguration(config) {
        return config
            .loadRelated(
                "stateData",
                "RawStateData",
                false,
                {},
                {config: true, capture: true, configObj: true, isConfigObj: true}
            )
            .then((rawConfigEntities) => {
                if (rawConfigEntities.length) {
                    //isConfigObj: devConf.isConfigObj
                    let conf = {};
                    conf.config = rawConfigEntities[0].capture;
                    conf.isConfigObj = rawConfigEntities[0].isConfigObj;
                    conf.configObj = JSON.parse(
                        JSON.stringify(_.get(rawConfigEntities[0], "configObj", {}))
                    );
                    // if (rawConfigEntities[0].isConfigObj) {
                    //     return JSON.stringify(rawConfigEntities[0].configObj, null, 2);
                    // }
                    // return rawConfigEntities[0].config;
                    return conf;
                }
            });
    }

    getViewModel() {
        return this._deviceConfigViewModel;
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = StateViewComponent;
StateViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.io",
    "$teve.solutionsManager",
    "$teve.entities.DeviceConfiguration",
    "$teve.storageFactory",
    "$teve.entities.Device",
];
StateViewComponent.$displayDetails = {
    type: "View",
    name: "State",
    icon: "configdrift.eye",
    shown: true,
    title: "State",
    order: 7,
};
